import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { AppService } from '@services/app.service';
import { UtilityService } from '@services/utility.service';
import { PermissionsService } from '@services/permissions.service';
import { PlantsNew } from '@/Interface/Register';
import { PlantStatusDDL } from '@/Interface/plants';
import { online } from '@/Interface/status';
import { StructuralDetailsModalComponent } from '@components/structural-details-modal/structural-details-modal.component';

// A lighter copy of the Plants list: only what is needed to pick a plant and
// open its profile (devices, ROI, panels). Same list API and filters, without
// the graphs, cluster and association actions.
@Component({
    selector: 'app-plant-profile-list',
    templateUrl: './plant-profile-list.component.html',
    styleUrls: ['./plant-profile-list.component.scss']
})
export class PlantProfileListComponent implements OnInit, OnDestroy {
    @ViewChild(StructuralDetailsModalComponent) structuralDetailsModal: StructuralDetailsModalComponent;

    plants: PlantsNew[] = [];
    plantCounts: online;
    tableLoader = false;
    isDownloading = false;

    searchElement = '';
    private searchSubject: Subject<string> = new Subject();
    private destroy$ = new Subject<void>();
    private queryParams: any = {};

    current_page = 1;
    next_page: number;
    total_pages: number;
    total_items: number;
    pageToGo: number = null;

    contractTypeList: any[] = [];
    regionList: any[] = [];
    cititiesList: any[] = [];
    selectedCity: any = null;
    regionLoading = false;
    citiesLoading = false;
    plantStatuslst: PlantStatusDDL[] = [
        new PlantStatusDDL(1, 'Offline'),
        new PlantStatusDDL(2, 'Faulty'),
        new PlantStatusDDL(3, 'Online')
    ];
    plantTypelst: PlantStatusDDL[] = [
        new PlantStatusDDL(1, 'Huawei'),
        new PlantStatusDDL(2, 'GoodWe'),
        new PlantStatusDDL(3, 'Deye')
    ];

    permissions = { canAdd: true, canEdit: true, canView: true, canDelete: true };
    permissionsReady = false;

    constructor(
        private appservice: AppService,
        private _utilityService: UtilityService,
        private permService: PermissionsService,
        private toastr: ToastrService,
        private _router: Router
    ) { }

    ngOnInit(): void {
        this.initComponent();
        this.getPlantCounts();
        this.getContractType();
        this.getRegion();
        this.getCities();
        this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe((term: string) => {
            this.searchElement = term;
            this.updateQueryParams({ search: term || null, page: null });
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async initComponent() {
        const url = this.permService.getCurrentUrl();
        const menuFromLocal = await this.permService.waitForMenuInLocalStorage();
        const matchedMenu = this.permService.findMenuPermissions(menuFromLocal, url);
        if (matchedMenu) {
            this.permissions = {
                canAdd: !!matchedMenu.canAdd,
                canEdit: !!matchedMenu.canEdit,
                canView: !!matchedMenu.canView,
                canDelete: !!matchedMenu.canDelete
            };
        }
        this.permissionsReady = true;
        if (this.permissions.canView) {
            this.getTableData();
        }
    }

    private updateQueryParams(paramsToUpdate: any): void {
        this.queryParams = { ...this.queryParams, ...paramsToUpdate };
        Object.keys(this.queryParams).forEach((key) => {
            if (this.queryParams[key] === null || this.queryParams[key] === '') {
                delete this.queryParams[key];
            }
        });
        this.getTableData();
    }

    private getTableData(): void {
        const q = this.queryParams;
        this.tableLoader = true;
        this.appservice
            .getAllPlantsdetails(
                q.search || '', q.page || 1, 10, null, null, q.type || null,
                q.status || null, q.contract_type_id || null, q.region_id || null, q.city_id || null
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    if (res && res['status'] === 200) {
                        this.plants = res.data.map((plant: any) => ({
                            ...plant,
                            capacity: Number(plant.capacity).toFixed(2),
                            currentPower: Number(plant.currentPower).toFixed(2),
                            energyToday: Number(plant.energyToday).toFixed(2)
                        }));
                        this.current_page = res.current_page;
                        this.next_page = res.next_page;
                        this.total_pages = res.total_pages;
                        this.total_items = res.total_records;
                    } else {
                        this.plants = [];
                    }
                    this.tableLoader = false;
                },
                error: () => {
                    this.plants = [];
                    this.tableLoader = false;
                }
            });
    }

    searchData(event: Event): void {
        this.searchSubject.next((event.target as HTMLInputElement)?.value || '');
    }

    selectRegion(region: any): void {
        this.selectedCity = null;
        this.updateQueryParams({ region_id: region?.id || null, city_id: null, page: null });
        this.getCitiesByRegion(region);
    }

    selectCity(city: any): void {
        this.updateQueryParams({ city_id: city || null, page: null });
    }

    selectStatus(statusId: number): void {
        this.updateQueryParams({ status: statusId || null, page: null });
    }

    selectContractType(contractType: any): void {
        this.updateQueryParams({ contract_type_id: contractType?.id || null, page: null });
    }

    selectPlantType(typeId: number): void {
        this.updateQueryParams({ type: typeId || null, page: null });
    }

    goToPage(page: number): void {
        if (page >= 1 && page <= this.total_pages) {
            this.updateQueryParams({ page });
        }
    }

    navigatePage(direction: 'prev' | 'next'): void {
        if (direction === 'prev' && this.current_page > 1) {
            this.goToPage(this.current_page - 1);
        } else if (direction === 'next' && this.next_page !== null) {
            this.goToPage(this.current_page + 1);
        }
    }

    openProfile(plant: PlantsNew): void {
        // Seed the service so the profile paints instantly; it re-fetches from
        // the :id route param when opened cold (deep link / refresh).
        this._utilityService.updateSelectedPlantName(plant.plantName);
        this._utilityService.setProduct(plant.stationCode);
        this._router.navigate(['/plant-management/plant-profile', plant.stationCode, 'devices']);
    }

    openStructuralDetails(plant: any, event: Event): void {
        // Keep the row click (open profile) from firing as well.
        event.stopPropagation();
        this.structuralDetailsModal.open({
            id: plant.stationCode,
            name: plant.plantName,
            region: plant.region_name,
            city: plant.city_name ?? plant.city
        });
    }

    downloadReport(): void {
        const q = this.queryParams;
        this.isDownloading = true;
        this._utilityService
            .downloadPlantReport({
                name: q.search || '',
                page: q.page || 1,
                size: 10,
                sort: null,
                column: null,
                type: q.type || null,
                status: q.status || null,
                contract_type_id: q.contract_type_id || null,
                region_id: q.region_id || null,
                city_id: q.city_id || null
            })
            .subscribe({
                next: (res: Blob) => {
                    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'Plant_Report.xlsx';
                    a.click();
                    window.URL.revokeObjectURL(url);
                    this.isDownloading = false;
                },
                error: () => {
                    this.isDownloading = false;
                    this.toastr.error('Failed to download report. Please try again.');
                }
            });
    }

    private getPlantCounts(): void {
        this.appservice.plantStatus().subscribe((res) => {
            this.plantCounts = res?.data;
        });
    }

    private getContractType(): void {
        this._utilityService.getContractTypeDropDown().subscribe((res) => {
            this.contractTypeList = res || [];
        });
    }

    private getRegion(): void {
        this.regionLoading = true;
        this._utilityService.getRegionDropDownList().subscribe({
            next: (res) => {
                this.regionList = res || [];
                this.regionLoading = false;
            },
            error: () => (this.regionLoading = false)
        });
    }

    private getCities(): void {
        this.citiesLoading = true;
        this._utilityService.getCitiesDropDown().subscribe({
            next: (res) => {
                this.cititiesList = res || [];
                this.citiesLoading = false;
            },
            error: () => {
                this.cititiesList = [];
                this.citiesLoading = false;
            }
        });
    }

    private getCitiesByRegion(region: any): void {
        const id = region?.id || null;
        this.cititiesList = [];
        if (!id) {
            this.getCities();
            return;
        }
        this.citiesLoading = true;
        this._utilityService.getCitiesByRegionDropDown(id).subscribe({
            next: (res) => {
                this.cititiesList = res || [];
                this.citiesLoading = false;
            },
            error: () => {
                this.cititiesList = [];
                this.citiesLoading = false;
            }
        });
    }
}
